// @ts-strict

import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { IChosenSortValue, IPagination, IStatisticsTiles } from '@app/core/models/core-models';
import { IStatisticsTableColumn, StatisticsCountryFilterItem, StatisticsTableItem, Option } from '@services/statistics/statistics.types';
import {
  FilterMenuOptions,
  GraphFilterOption,
  StatisticsColumnManagmentNumberType,
  StatisticsMapItem,
  StatsRequestParams,
  StatsRequestParamsMap
} from '@pages/statistics/shared/interfaces/statistics';
import { StatisticsSummaryService } from '@services/statistics/statistics-summary.service';
import { SearchInputHandler } from '@services/statistics/statistics.types';
import { isArray, isEmpty, uniqueId } from 'lodash';
import { Observable, Subject, Subscription } from 'rxjs';
import { filter, skip, takeUntil, tap } from 'rxjs/operators';
import { AvailableStatisticsTableColumns, TableService } from '../statistics-table/table.service';
import { SharedStatisticsFunctionsService } from './shared-statistics-functions.service';
import { StatisticsService } from '@pages/statistics/statistics.service';
import { ISelectOption } from '@app/shared/types/inputs';

export type ColumnManagementOptionsType = {
  subcategory: 'leads' | 'earnings' | 'others';
  results: IStatisticsTableColumn[];
}[];
@Component({
  selector: 'app-shared-statistics-functions',
  templateUrl: './shared-statistics-functions.component.html',
  styleUrls: ['./shared-statistics-functions.component.scss']
})
export class SharedStatisticsFunctionsComponent implements OnInit, OnDestroy {
  @Input() tilesData: IStatisticsTiles[] = [];
  @Input() tilesCompareData: IStatisticsTiles[] = [];
  @Input() statisticsCompareData: StatisticsTableItem[] = [];
  @Input() statisticsData: StatisticsTableItem[] = [];
  @Input() mapData: StatisticsMapItem[] = [];
  @Input() mapCompareData: StatisticsMapItem[] = [];
  @Input() isLoaded = false;
  @Input() title = '';
  @Input() paramToCompare = '';
  @Input() compareDates?: { date_to: string; date_from: string };
  @Input() defaultVisibleColumns: AvailableStatisticsTableColumns[] = [];
  @Input() dropdownValues: IChosenSortValue[] = [];
  @Input() requestParams!: StatsRequestParams;
  @Input() pagination?: IPagination;
  @Input() loadTableDataOnPageChange = false;
  @Input() firstStatisticsFetch = true;
  @Input() requestParamsMap!: StatsRequestParamsMap;
  @Input() columnManagementNumbers?: StatisticsColumnManagmentNumberType;
  @Input() graphFilterOptions: GraphFilterOption[] = [];
  @Input() filterMenuOptions?: FilterMenuOptions;
  @Input() page = 1;
  @Input() pageSize = 10;
  @Input() inputPage = 1;
  @Input() widgetMode = false;
  @Input() tilesLoading = false;

  @Input() graphAndMapContainerTitle = 'earnings_visualization';
  @Input() graphAndMapContainerTotalElements = '';
  @Input() graphAndMapContainerHeaderIcon = '';

  @Output() emitDataFetchEvent = new EventEmitter<StatsRequestParams>();
  @Output() emitDateChange = new EventEmitter<StatsRequestParams>();
  @Output() emitPageChange = new EventEmitter<StatsRequestParams>();

  public selectedCountries: string[] = [];
  public appliedFiltersBar: { [x: string]: any; countries?: any; admin_program_id?: any } = {};
  public datesDifference = 0;
  public compareToCurrentDatesDifference = 0;
  public options: ColumnManagementOptionsType = [];

  private subscription = new Subscription();
  private defaultFilters: FilterMenuOptions = {};

  private destroy$ = new Subject<void>();

  get dailyStats(): boolean {
    return this.title === 'stats_daily';
  }

  constructor(
    private readonly statistics: StatisticsSummaryService,
    private readonly statisticsTableService: TableService,
    private readonly sharedStatisticsFunctionsService: SharedStatisticsFunctionsService,
    private readonly statisticsService: StatisticsService
  ) {}

  ngOnInit(): void {
    if (this.filterMenuOptions) {
      this.defaultFilters = this.filterMenuOptions;
    }

    if (this.columnManagementNumbers) {
      this.options = this.statisticsTableService.setColumnsOptions(this.columnManagementNumbers, this.defaultVisibleColumns);
      this.statisticsTableService.setTableColumns(this.options);
    }

    this.sharedStatisticsFunctionsService
      .watchRefetch()
      .pipe(
        tap(() => this.emitDataFetchEvent.emit(this.requestParams)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.emitDataFetchEvent
      .pipe(
        tap(() => this.statisticsService.setStatisticsRequestParams(this.requestParams)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.emitDateChange
      .pipe(
        tap(() => this.statisticsService.setStatisticsRequestParams(this.requestParams)),
        takeUntil(this.destroy$)
      )
      .subscribe();

    this.statistics.setSelectedCampaigns([]); // selected incent campaigns were bugging from being delete on reset
    this.statistics.setSelectedAdminCampaigns([]); // selected incent campaigns were bugging from being delete on reset

    this.watchForDateChange();
    this.watchForSortMethods();
    this.watchForSelectedOptions();
    this.watchForSearchInput().subscribe();
    this.watchForResetAll();
    this.watchForSlider();
    this.watchForSelectedCountries().subscribe();
    this.watchForSelectedCampaigns().subscribe();
    this.watchForSelectedAdminCampaigns().subscribe();
    this.watchForCleanFilters();
    this.removedFromAppliedFilters();
  }

  ngOnDestroy(): void {
    this.statistics.dateRangeDifference = 365;
    this.statistics.voidResetFiltersOnDestroy();

    this.statistics.allColumns.map(el => {
      const item = { column: el, checked: false };
      this.statistics.setColumnsVisibility(item);
    });
    this.subscription.unsubscribe();
    this.statistics.currentSelectedOptions = {};
    this.destroy$.next();
    this.destroy$.complete();
    this.statisticsService.resetStatisticsRequestParams();
  }

  public pageChange(page: number): void {
    this.page = page;
    this.requestParams.page = page;
    this.emitPageChange.emit(this.requestParams);
  }

  public itemsPerPageChange(itemsPerPage: number): void {
    this.requestParams.per_page = itemsPerPage;
    this.emitPageChange.emit(this.requestParams);
  }

  private watchForSelectedCountries(): Observable<StatisticsCountryFilterItem[]> {
    return this.statistics.selectedCountry.pipe(
      filter(selectedCountries => typeof selectedCountries !== 'boolean'),
      tap(selectedCountries => {
        if (!isEmpty(selectedCountries)) {
          this.appliedFiltersBar = { ...this.appliedFiltersBar, countries: selectedCountries };
          this.requestParams = { ...this.requestParams, 'countries[]': [...selectedCountries.map(({ code }) => code)] };
        }

        if (isEmpty(selectedCountries)) {
          const { countries, ...rest } = this.appliedFiltersBar;

          this.appliedFiltersBar = { ...rest };
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private watchForSelectedCampaigns(): Observable<ISelectOption[]> {
    return this.statistics.selectedCampaigns.pipe(
      filter(selectedCampaigns => typeof selectedCampaigns !== 'boolean'),
      tap(selectedCampaigns => {
        if (!isEmpty(selectedCampaigns)) {
          this.appliedFiltersBar = { ...this.appliedFiltersBar, program_id: selectedCampaigns };
          this.requestParams = { ...this.requestParams, 'programs[]': [...selectedCampaigns.map(({ id }) => id.toString())].toString() };
        }

        if (isEmpty(selectedCampaigns)) {
          const { program_id, ...rest } = this.appliedFiltersBar;

          this.appliedFiltersBar = { ...rest };
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private watchForSelectedAdminCampaigns(): Observable<ISelectOption[]> {
    return this.statistics.selectedAdminCampaigns.pipe(
      filter(selectedCampaigns => typeof selectedCampaigns !== 'boolean'),
      tap(selectedCampaigns => {
        if (!isEmpty(selectedCampaigns)) {
          this.appliedFiltersBar = { ...this.appliedFiltersBar, admin_program_id: selectedCampaigns };
          this.requestParams = { ...this.requestParams, 'admin_programs[]': [...selectedCampaigns.map(({ id }) => id.toString())] };
        }

        if (isEmpty(selectedCampaigns)) {
          const { admin_program_id, ...rest } = this.appliedFiltersBar;

          this.appliedFiltersBar = { ...rest };
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private watchForSearchInput(): Observable<SearchInputHandler | undefined> {
    return this.statistics.searchInputHandler.pipe(
      tap(res => {
        if (res?.inputValue) {
          let currentParam: string | undefined = '';
          if (res.searchValueName === 'admin_program_id') {
            currentParam = 'admin_programs[]';
          } else {
            currentParam = this.requestParamsMap[res.menuName as keyof StatsRequestParamsMap];
          }

          if (!currentParam) {
            currentParam = res.searchValueName;
          }

          this.requestParams = { ...this.requestParams, page: 1, [currentParam]: res.inputValue };

          if (this.appliedFiltersBar[res.searchValueName]) {
            if (this.appliedFiltersBar[res.searchValueName].find((item: { text: string }) => item.text === res.inputValue)) return;

            this.appliedFiltersBar = {
              ...this.appliedFiltersBar,
              [res.searchValueName]: [
                ...this.appliedFiltersBar[res.searchValueName],
                { text: res.inputValue, noTranslation: true, currentParam: res.menuName, fieldType: 'search', id: uniqueId() }
              ]
            };

            this.requestParams = {
              ...this.requestParams,
              [currentParam]: [...this.appliedFiltersBar[res.searchValueName].map((item: { text: string }) => item.text)]
            };

            return;
          }

          this.appliedFiltersBar = {
            ...this.appliedFiltersBar,
            [res.searchValueName]: [{ text: res.inputValue, noTranslation: true, currentParam: res.menuName, fieldType: 'search', id: uniqueId() }]
          };

          if (!res.inputValue) delete this.requestParams[currentParam as keyof StatsRequestParams];
        }
      }),
      takeUntil(this.destroy$)
    );
  }

  private watchForSlider(): void {
    const sliderValues = this.statistics.sliderValues.subscribe((res: any) => {
      if (res && res.rangeData) {
        if (!this.areSliderValuesTheSameAsThanDefault(res.rangeData.filterOption)) {
          const currentUnit = typeof res.rangeData.currency === 'undefined' ? 'EUR' : res.rangeData.currency;
          this.appliedFiltersBar[res.rangeData.filterOption.name] = [
            {
              text: `${res.values[0]} - ${res.values[1]} ${currentUnit}`,
              noTranslation: true,
              fieldType: res.rangeData.filterOption.fieldType
            }
          ];

          this.requestParams = {
            ...this.requestParams,
            [res.rangeData.filterOption.paramFrom]: res.values[0],
            [res.rangeData.filterOption.paramTo]: res.values[1]
          };
          this.page = 1;
        } else {
          delete this.appliedFiltersBar[res.rangeData.filterOption.name];
          delete this.requestParams[res.rangeData.filterOption.paramFrom as keyof StatsRequestParams];
          delete this.requestParams[res.rangeData.filterOption.paramTo as keyof StatsRequestParams];
          this.page = 1;
        }
      }
    });
    this.subscription.add(sliderValues);
  }

  private areSliderValuesTheSameAsThanDefault(sliderOptions: any): boolean {
    return sliderOptions.currentMaxValue === sliderOptions.maxValue && sliderOptions.currentMinValue === sliderOptions.minValue;
  }

  /**
   * Watch for cleanFilters event - when user clicks trash bin button on single column-menu popup
   */
  private watchForCleanFilters(): void {
    this.statistics.cleanFilters.subscribe((res: any) => {
      this.filterMenuOptions?.[res].map(option => {
        switch (option.category) {
          case 'searchInput':
            option.values = null;
            this.requestParams[this.requestParamsMap[res as keyof StatsRequestParamsMap] as keyof StatsRequestParams]
              ? delete this.requestParams[this.requestParamsMap[res as keyof StatsRequestParamsMap] as keyof StatsRequestParams]
              : null;
            break;
          case 'checkboxesList':
            Object.keys(option.values).map(key => {
              option.values[key] = JSON.parse(JSON.stringify(option.values[key]).replace(/"checked":false/g, '"checked":true'));
              this.requestParams[this.requestParamsMap[key as keyof StatsRequestParamsMap] as keyof StatsRequestParams]
                ? delete this.requestParams[this.requestParamsMap[key as keyof StatsRequestParamsMap] as keyof StatsRequestParams]
                : null;
              delete this.appliedFiltersBar[key];
            });

            const category = Object.keys(option.values)[0];

            this.statistics.currentSelectedOptions[category] = this.statistics.currentSelectedOptions[category].map(item => ({ ...item, checked: true }));

            break;
          case 'countriesSelect':
            delete this.requestParams['countries[]'];
            delete this.appliedFiltersBar.countries;

            this.statistics.setSelectedCountry([]);
            break;
          case 'slider':
            if (this.filterMenuOptions) {
              const currentFilterOption = this.filterMenuOptions[res].find(filter => filter.category === 'slider');

              if (currentFilterOption) {
                currentFilterOption.currentMinValue = currentFilterOption.minValue;
                currentFilterOption.currentMaxValue = currentFilterOption.maxValue;

                if (currentFilterOption.paramFrom) delete this.requestParams[currentFilterOption.paramFrom as keyof StatsRequestParams];
                if (currentFilterOption.paramTo) delete this.requestParams[currentFilterOption.paramTo as keyof StatsRequestParams];
                if (currentFilterOption.name) delete this.appliedFiltersBar[currentFilterOption.name];
              }
            }
        }
      });
      this.page = 1;
      this.emitDataFetchEvent.emit(this.requestParams);
    });
  }

  /**
   * Activated when user removes option by clicking trash bin button in applied filters bar
   */
  private removedFromAppliedFilters(): void {
    const removedFromFilters = this.statistics.removeOptionFromBar.subscribe(res => {
      // console.log(res);
      // console.log(res.removedOption.type);
      // console.log(this.requestParamsMap);
      const currentRequestParams = this.requestParams[this.requestParamsMap[res.removedOption.type as keyof StatsRequestParamsMap] as keyof StatsRequestParams];
      if (typeof currentRequestParams !== 'number' && currentRequestParams?.length && isArray(currentRequestParams)) {
        switch (res.removedOption.fieldType) {
          case 'checkbox':
            this.requestParams = {
              ...this.requestParams,
              [this.requestParamsMap[res.removedOption.type as keyof StatsRequestParamsMap] as string]: (currentRequestParams as any).filter(
                (elem: any) => elem !== res.removedOption.id
              )
            };

            this.statistics.currentSelectedOptions[res.removedOption.category] = this.statistics.currentSelectedOptions[res.removedOption.category].map(item =>
              item.id === res.removedOption.id ? { ...item, checked: false } : item
            );

            if (this.statistics.currentSelectedOptions[res.removedOption.category].every(({ checked }) => !checked)) {
              this.statistics.currentSelectedOptions[res.removedOption.category] = this.statistics.currentSelectedOptions[res.removedOption.category].map(
                item => ({ ...item, checked: true })
              );
            }

            break;
          case 'select':
            this.requestParams = {
              ...this.requestParams,
              [this.requestParamsMap[res.removedOption.type as keyof StatsRequestParamsMap] as string]: (currentRequestParams as any).filter(
                (elem: any) => elem !== res.removedOption.id
              )
            };
            break;
          case 'search':
            this.requestParams = {
              ...this.requestParams,
              [this.requestParamsMap[res.removedOption.type as keyof StatsRequestParamsMap] as string]: (currentRequestParams as any).filter(
                (elem: any) => elem !== res.removedOption.text
              )
            };
            break;
          default:
            this.requestParams = {
              ...this.requestParams,
              [this.requestParamsMap[res.removedOption.type as keyof StatsRequestParamsMap] as string]: (currentRequestParams as any).filter(
                (elem: any) => elem !== res.removedOption.id.toString()
              )
            };
            break;
        }
        if (!currentRequestParams.length) {
          delete this.requestParams[this.requestParamsMap[res.removedOption.type as keyof StatsRequestParamsMap] as keyof StatsRequestParams];
        }
      }
      if (res.removedOption.fieldType === 'search') {
        // console.log(res.removedOption);
        switch (res.removedOption.type) {
          case 'userNameMenu':
            delete this.requestParams.username;
            break;
          case 'campaignMenu':
            delete this.requestParams['programs[]'];
            break;
          case 'configurationNameMenu':
            delete this.requestParams['configurations[]'];
            break;
          default:
            break;
        }
      }
      if (res.removedOption.fieldType === 'slider' && this.filterMenuOptions) {
        const currentMenuName = res.removedOption.type.toLowerCase();
        const currentMenu = `${currentMenuName}Menu`;
        const currentFilterOption = this.filterMenuOptions[currentMenu].find(option => option.category === 'slider');

        if (currentFilterOption) {
          currentFilterOption.currentMinValue = currentFilterOption.minValue;
          currentFilterOption.currentMaxValue = currentFilterOption.maxValue;
          if (currentFilterOption.paramFrom) delete this.requestParams[currentFilterOption.paramFrom as keyof StatsRequestParams];
          if (currentFilterOption.paramTo) delete this.requestParams[currentFilterOption.paramTo as keyof StatsRequestParams];
        }
      }
      this.page = 1;
      this.emitDataFetchEvent.emit(this.requestParams);
    });
    this.subscription.add(removedFromFilters);
  }

  private watchForDateChange(): void {
    const dateRangeSubject = this.statistics.dateRange.pipe(skip(1)).subscribe(dateRange => {
      this.requestParams.date_from = `${dateRange.dateFrom.year}-${dateRange.dateFrom.month}-${dateRange.dateFrom.day}`;
      this.requestParams.date_to = `${dateRange.dateTo.year}-${dateRange.dateTo.month}-${dateRange.dateTo.day}`;
      this.page = 1;
      this.emitDateChange.emit({ ...this.requestParams, page: 1 });
    });
    this.subscription.add(dateRangeSubject);
  }

  private watchForSortMethods(): void {
    const sortMethods = this.statistics.sortMethod.subscribe(method => {
      if (method) {
        this.requestParams.order_field = method.sortMethod.orderField || 'program_id';
        this.requestParams.order_type = method.sortMethod.orderType || 'desc';
        this.page = 1;
      }
      if (method.origin === 'header') {
        this.emitDataFetchEvent.emit({ ...this.requestParams, page: 1 });
      }
    });
    this.subscription.add(sortMethods);
  }

  private watchForSelectedOptions(): void {
    const selectedOptionsSubject = this.statistics.setSelectedOptions.subscribe((selectedOptions: Record<string, Option[]>) => {
      const category = Object.keys(selectedOptions)[0];

      if (selectedOptions[category].every(({ checked }) => checked)) {
        delete this.requestParams[this.requestParamsMap[category as keyof StatsRequestParamsMap] as keyof StatsRequestParams];
        delete this.appliedFiltersBar[category];

        this.statistics.currentSelectedOptions = { ...this.statistics.currentSelectedOptions, ...selectedOptions };

        this.page = 1;

        return;
      }

      this.requestParams = {
        ...this.requestParams,
        [this.requestParamsMap[category as keyof StatsRequestParamsMap] as string]: [
          ...selectedOptions[category].filter(({ checked }) => checked).map(({ id }) => id)
        ]
      };
      this.appliedFiltersBar = { ...this.appliedFiltersBar, [category]: selectedOptions[category].filter(({ checked }) => checked) };

      this.statistics.currentSelectedOptions = { ...this.statistics.currentSelectedOptions, ...selectedOptions };

      this.page = 1;
    });
    this.subscription.add(selectedOptionsSubject);
  }

  /**
   * Activated when user click 'remove all filters' text in applied filters bar
   */
  private watchForResetAll(): void {
    const resetAll = this.statistics.resetAll.subscribe(_ => {
      this.filterMenuOptions = JSON.parse(JSON.stringify(this.defaultFilters));
      this.setRequestParamsToInitialValues();
      this.statistics.currentSelectedOptions = {};
      this.appliedFiltersBar = {};
      this.statistics.setSelectedCountry([]);
      this.statistics.setSelectedCampaigns([]);
      this.statistics.setSelectedAdminCampaigns([]);
    });
    this.subscription.add(resetAll);
  }

  private setRequestParamsToInitialValues(): void {
    if (this.requestParams) {
      this.requestParams = {
        date_from: this.requestParams.date_from,
        date_to: this.requestParams.date_to
      };
    }
    // this.loadTableDataOnPageChange = false;
    this.page = 1;
    this.emitDataFetchEvent.emit(this.requestParams);
  }
}
